import React, { useState, useEffect } from 'react';
import {Box, Typography, IconButton, Button, useMediaQuery, useTheme } from '@mui/material';
import Grid from '@mui/material/Grid';
import CloseIcon from '@mui/icons-material/Close';
import ManagedModal from './ManagedModal';
//import AuthContext from "flexbiz-core/components/auth/Context";
import BaseFieldControl from './BaseFieldControl';
import { getDateRangeByType } from './DateFilterInput';

const IOS_BLUE = '#007aff';

export default function DataListFilter({ 
  open, 
  onClose, 
  fields, 
  onApplyFilter,
  currentFilters = {}, 

  dateField=null,
  currentDateFilter = null 
}) {
  
  // 1. Lọc các trường hợp lệ để tìm kiếm (Giữ nguyên tư duy Whitelist)
  const searchableFields = fields.filter(f => f.searchable !== false);
  
  // 2. Hàm khởi tạo State chuẩn xác
  const getInitFilters = () => {
    return searchableFields.reduce((acc, field) => {
      // --- XỬ LÝ SỐ (NUMBER) ---
      if (field.type === 'Number') {
        acc[field.field] = { min: '', max: '' };
      } 
      
      // --- XỬ LÝ THỜI GIAN (DATE / DATETIME / DATEFILTER) ---
      else if (dateField && field.field === dateField.field) {
        const fromKey = field.mapTo?.[0] || 'fromDate';
        const toKey = field.mapTo?.[1] || 'toDate';

        const defaultType = field.default?.type || 'thisMonth';
        let from = field.default?.[fromKey] || '';
        let to = field.default?.[toKey] || '';

        // SỬ DỤNG HÀM TIỆN ÍCH ĐỂ DỊCH TYPE THÀNH NGÀY
        if (defaultType && defaultType !== 'custom' && defaultType !== 'all') {
            const range = getDateRangeByType(defaultType);
            from = range.fromDate;
            to = range.toDate;
        }

        acc[field.field] = { type: defaultType, [fromKey]: from, [toKey]: to };
      }
      // Xử lý an toàn cho các trường Date "phụ" khác (nếu form có nhiều trường ngày)
      else if (field.type === 'Date' || field.type === 'DateTime' || field.type === 'DateFilter') {
        const fromKey = field.mapTo?.[0] || 'fromDate';
        const toKey = field.mapTo?.[1] || 'toDate';
        acc[field.field] = { type: 'all', [fromKey]: '', [toKey]: '' };
      }
      
      // --- XỬ LÝ CÁC TRƯỜNG CÒN LẠI ---
      else {
        // Tôn trọng giá trị default nếu có khai báo, không thì để chuỗi rỗng
        acc[field.field] = field.default !== undefined ? field.default : ''; 
      }
      
      return acc;
    }, {});
  };
  
  const [advancedFilters, setAdvancedFilters] = useState(getInitFilters());

  // 3. ĐỒNG BỘ STATE: Nạp lại bộ lọc đang áp dụng mỗi khi mở Drawer
  useEffect(() => {
    if (open) {
      const defaultFilters = getInitFilters();
      
      // Khôi phục lại các trường text/number/boolean
      const mergedFilters = { ...defaultFilters, ...currentFilters };
      
      // 3. SỬ DỤNG TRỰC TIẾP DATE FIELD ĐỂ NẠP LẠI GIÁ TRỊ CŨ
      if (dateField && currentDateFilter) {
          mergedFilters[dateField.field] = currentDateFilter; 
      }

      setAdvancedFilters(mergedFilters);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);


  // ================= HANDLERS =================
  const handleClear = () => {
    const clearedFilters = getInitFilters();
    setAdvancedFilters(clearedFilters);
    onApplyFilter({}, null); // Truyền object rỗng thay vì null để hệ thống đỡ lỗi undefined
    onClose();
  };

  const handleApply = () => {
    
    let dateFilters = null;
    let textFilters = { ...advancedFilters };

    if (dateField) {
      const dateVal = advancedFilters[dateField.field];
      if (dateVal && dateVal.type !== 'all') {
        dateFilters = dateVal; 
      }
      delete textFilters[dateField.field]; // Tách ra khỏi textFilters
    }

    const cleanedTextFilters = {};
    Object.keys(textFilters).forEach(key => {
        const val = textFilters[key];
        if (val !== '' && val !== null && val !== undefined) {
            if (Array.isArray(val) && val.length === 0) return;
            if (typeof val === 'object' && val.min === '' && val.max === '') return;
            cleanedTextFilters[key] = val;
        }
    });

    // Đẩy chính xác 2 khối dữ liệu lên DataListManager / Server
    onApplyFilter(cleanedTextFilters, dateFilters);
    onClose();
  };

  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('sm')); // Từ tablet/PC trở lên

  return (
    <ManagedModal
      type="drawer" 
      anchor={isDesktop ? "right" : "bottom"}
      open={open} onClose={onClose}
      PaperProps={{ 
        sx: isDesktop 
          ? { 
              // --- STYLE CHO DESKTOP (Floating Right Panel) ---
              width: 380, // Giới hạn chiều rộng bằng đúng 1 cái màn hình Mobile
              borderRadius: 3, // Bo góc cả 4 chiều
              m: 2, // Cách mép màn hình 16px tạo cảm giác trôi nổi
              height: 'auto', 
              maxHeight: 'calc(100vh - 32px)', // Tránh đụng viền trên dưới
              display: 'flex', 
              flexDirection: 'column',
              overflow: 'hidden'
            }
          : { 
              // --- STYLE CHO MOBILE (Bottom Sheet) ---
              borderTopLeftRadius: 24, 
              borderTopRightRadius: 24, 
              pb: 'max(24px, env(safe-area-inset-bottom))', 
              maxHeight: '85vh',
              width: '100%',
              display: 'flex', 
              flexDirection: 'column'
            } 
      }}
    >
      <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', height: '100%' }}>
        {/* CÁI VẠCH NGANG KÉO THẢ (Chỉ hiện trên Mobile) */}
        {!isDesktop && (
          <Box sx={{ display: 'flex', justifyItems: 'center', justifyContent: 'center', mb: 2 }}>
            <Box sx={{ width: 40, height: 4, bgcolor: 'divider', borderRadius: 2 }} />
          </Box>
        )}
        {/* HEADER */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2}}>
          <Typography variant="h6" fontWeight="bold">Lọc nâng cao</Typography>
          <IconButton onClick={onClose} size="small" sx={{ bgcolor: 'action.hover' }}><CloseIcon /></IconButton>
        </Box>
        {/* NỘI DUNG FORM (Cuộn được nếu quá dài) */}
        <Box sx={{ overflowY: 'auto', px: 0.5, flexGrow: 1, mx: -0.5,pt:1 }}>
          <Grid container spacing={2}>
            
            {searchableFields.map((f) => {
              
              // 1. "Phù phép" Component Type
              let filterFieldConfig = { ...f };
              if (f.type === 'Number') filterFieldConfig.type = 'NumberRange';
              
              // Ép kiểu DateFilter cho cả trường Date chính lẫn phụ
              if ((dateField && f.field === dateField.field) || f.type === 'Date' || f.type === 'DateTime') {
                  filterFieldConfig.type = 'DateFilter';
              }
              
              // BÍ QUYẾT TẠI ĐÂY: Xử lý trường Boolean thành Select 3 trạng thái
              if (f.type === 'Boolean') {
                  filterFieldConfig.type = 'Select';
                  filterFieldConfig.options = [
                      { label: 'Tất cả', value: '' },
                      { label: 'Có', value: true },
                      { label: 'Không', value: false }
                  ];
              }

              // 2. Tính toán độ rộng của cột
              const defaultSize = { xs: 12, sm: 12 }
              const gridSize = f.gridFilter || defaultSize;

              return (
                <Grid size={gridSize} key={f.field}>
                  <BaseFieldControl 
                    field={filterFieldConfig}
                    value={advancedFilters[f.field]}
                    onChange={(val) => setAdvancedFilters({...advancedFilters, [f.field]: val})}
                    formData={advancedFilters}
                  />
                </Grid>
              );
            })}
            
          </Grid>
        </Box>
        {/* FOOTER: NÚT BẤM (Ghim cố định ở đáy panel) */}
        <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid #f0f0f0', display: 'flex', gap: 1.5, flexShrink: 0 }}>
          <Button variant="outlined" color="inherit" fullWidth onClick={handleClear} sx={{ borderRadius: 2, textTransform: 'none' }}>
            Xóa lọc
          </Button>
          <Button variant="contained" color="primary" fullWidth onClick={handleApply} sx={{ borderRadius: 2, bgcolor: IOS_BLUE, textTransform: 'none' }}>
            Áp dụng
          </Button>
        </Box>
      </Box>
    </ManagedModal>
  );
}